/**
 * Willtec Predictive Search Custom Element
 * Connects header search inputs to Shopify Native Predictive Search API (/search/suggest?section_id=predictive-search).
 * Supports debouncing (250ms), AbortController, clear button X, keyboard navigation (combobox ARIA), and click outside logic.
 */
(() => {
  if (customElements.get('willtec-predictive-search')) return;

  class WilltecPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = new Map();
      this.abortController = null;
      this.debounceTimer = null;
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.searchBtn = this.querySelector('[data-search-submit]');
      this.resultsContainer = this.querySelector('[data-predictive-search-results]');

      if (!this.input || !this.resultsContainer) return;

      this.syncInitialQuery();
      this.bindEvents();
    }

    syncInitialQuery() {
      const searchUrl = this.getAttribute('data-search-url') || this.form?.getAttribute('action') || window.Shopify?.routes?.search_url || '/search';
      const isSearchRoute = window.location.pathname.endsWith('/search') || window.location.pathname === searchUrl;

      if (isSearchRoute) {
        const params = new URLSearchParams(window.location.search);
        const query = params.get('q');
        if (query !== null && query !== undefined) {
          const trimmed = query.trim();
          if (trimmed === '*') {
            this.input.value = '';
          } else if (!this.input.value || this.input.value !== query) {
            this.input.value = query;
          }
        }
      }

      if (this.input.value.trim() === '*') {
        this.input.value = '';
      }

      this.updateClearButtonState();
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.handleInput());
      this.input.addEventListener('focus', () => this.handleFocus());
      this.input.addEventListener('keydown', (e) => this.handleKeyDown(e));

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.clear();
        });
      }

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
          this.input.focus();
        }
      });
    }

    handleInput() {
      const query = this.input.value.trim();
      this.updateClearButtonState();

      if (query.length < 2) {
        this.close();
        return;
      }

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchResults(query);
      }, 250);
    }

    handleFocus() {
      const query = this.input.value.trim();
      if (query.length >= 2) {
        if (this.cachedResults.has(query)) {
          this.renderResults(this.cachedResults.get(query));
          this.open();
        } else {
          this.fetchResults(query);
        }
      }
    }

    updateClearButtonState() {
      const hasValue = this.input.value.trim().length > 0;
      if (this.clearBtn) {
        if (hasValue) {
          this.clearBtn.classList.remove('hidden');
          if (this.searchBtn) this.searchBtn.classList.add('hidden');
        } else {
          this.clearBtn.classList.add('hidden');
          if (this.searchBtn) this.searchBtn.classList.remove('hidden');
        }
      }
    }

    clear() {
      this.input.value = '';
      this.updateClearButtonState();
      this.close();
      this.input.focus();
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    async fetchResults(query) {
      if (this.cachedResults.has(query)) {
        this.renderResults(this.cachedResults.get(query));
        this.open();
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.setAttribute('aria-busy', 'true');

      try {
        const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=3&section_id=predictive-search`;
        const response = await fetch(url, { signal: this.abortController.signal });

        if (!response.ok) return;

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const resultsHTML = doc.getElementById('predictive-search-results');

        if (resultsHTML) {
          const html = resultsHTML.innerHTML;
          this.cachedResults.set(query, html);
          this.renderResults(html);
          this.open();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Willtec Predictive Search Error:', error);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    renderResults(html) {
      this.resultsContainer.innerHTML = html;
    }

    open() {
      this.resultsContainer.classList.remove('hidden');
      this.input.setAttribute('aria-expanded', 'true');

      // Close Mega Menu if open
      document.querySelectorAll('.willtec-mega-menu-panel').forEach(p => p.classList.add('invisible', 'opacity-0'));
    }

    close() {
      this.resultsContainer.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'false');
    }

    isOpen() {
      return !this.resultsContainer.classList.contains('hidden');
    }

    handleKeyDown(e) {
      if (!this.isOpen()) return;

      const options = Array.from(this.resultsContainer.querySelectorAll('[role="option"], a'));
      if (options.length === 0) return;

      let activeIndex = options.findIndex(opt => opt === document.activeElement || opt.classList.contains('bg-[#F9F9F9]'));

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = activeIndex < options.length - 1 ? activeIndex + 1 : 0;
        options[activeIndex].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = activeIndex > 0 ? activeIndex - 1 : options.length - 1;
        options[activeIndex].focus();
      }
    }
  }

  if (!customElements.get('willtec-predictive-search')) {
    customElements.define('willtec-predictive-search', WilltecPredictiveSearch);
  }
})();
